import { Injectable } from '@angular/core';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

const DEFAULT_AVATAR = 'default-avatar.png';
const DEFAULT_USER_PHOTO_PATH = 'assets/user_image.jpg';

@Injectable({
  providedIn: 'root'
})
export class MediaCacheService {
  private readonly storage = getStorage();
  private readonly urlCache = new Map<string, string>();

  async getCachedUrl(storagePath: string): Promise<string | null> {
    const cached = this.urlCache.get(storagePath);
    if (cached) {
      return cached;
    }

    try {
      const url = await getDownloadURL(ref(this.storage, storagePath));
      this.urlCache.set(storagePath, url);
      return url;
    } catch {
      return null;
    }
  }

  async getPostImageUrls(photos: string[] | undefined): Promise<string[]> {
    if (!photos?.length) {
      return [];
    }

    const urls = await Promise.all(
      photos.map((photo) => this.getCachedUrl(`spots/${photo}`))
    );

    return urls.filter((url): url is string => url !== null);
  }

  async getUserProfilePhotoUrl(userId: string): Promise<string> {
    const path = `users/${userId}/user_photo.jpg`;
    const url = await this.getCachedUrl(path);
    return url ?? DEFAULT_AVATAR;
  }

  async getTeamCoverUrl(teamId: string, storedUrl?: string | null): Promise<string | null> {
    if (storedUrl) {
      return storedUrl;
    }

    const cover = await this.getCachedUrl(`teams/${teamId}/cover.jpg`);
    if (cover) {
      return cover;
    }

    return this.getCachedUrl(DEFAULT_USER_PHOTO_PATH);
  }

  async getTeamBadgeUrl(teamId: string, storedUrl?: string | null): Promise<string | null> {
    if (storedUrl) {
      return storedUrl;
    }

    return this.getCachedUrl(`teams/${teamId}/badge.jpg`);
  }

  async getEventCoverUrl(eventId: string, storedUrl?: string | null): Promise<string | null> {
    if (storedUrl) {
      return storedUrl;
    }

    const cover = await this.getCachedUrl(`events/${eventId}/cover.jpg`);
    if (cover) {
      return cover;
    }

    return this.getCachedUrl(DEFAULT_USER_PHOTO_PATH);
  }
}
